package com.oasisfeng.invaluable.social

/** Shared constants and the transport seam for zero-config local peer discovery (see [PeerDiscovery]). */
object Signaling {
    /** Well-known localhost port for the unified WebSocket signaling relay shared by every form (TUI / MCP / Web). */
    const val DEFAULT_PORT: Int = 51900
    /** BroadcastChannel room name reaching this browser's own same-origin tabs. */
    const val ROOM: String = "invaluable-social"
}

/**
 * A signaling message bus: carries the discovery protocol's presence / offer / answer messages (plain JS objects)
 * between local nodes. Implementations differ only in *transport* — a same-origin [BroadcastChannelBus], the localhost
 * WebSocket relay ([NodeSignalingHub] on Node, [WebSocketBus] in the browser) — never in the *protocol*, which lives
 * entirely in [PeerDiscovery]. This is the seam that lets every form (TUI / MCP / Web) share one discovery engine.
 */
interface SignalingBus {
    /** Short transport label for status lines / logs (e.g. `ws://127.0.0.1:51900`, `same-origin tabs`). */
    val label: String
    /** Whether this transport is usable in the current runtime (e.g. `BroadcastChannel` / `WebSocket` exists). */
    val available: Boolean
    /** Begin delivering inbound messages. A bus must never echo a node's own [post] back to it. */
    fun start(onMessage: (dynamic) -> Unit)
    /** Broadcast a message object to every other node on the bus. */
    fun post(message: dynamic)
    fun close()
}

/**
 * Fans a single [PeerDiscovery] engine across several transports at once: the browser runs a [BroadcastChannelBus]
 * (tab ↔ tab, always) together with a [WebSocketBus] (to reach Node forms when a hub is up). Outbound messages go to
 * every available child; inbound messages from any child feed the one engine. The protocol is idempotent to the
 * duplicates this can produce (presence is a map upsert; offers/answers are keyed and guarded), so a peer reachable on
 * two buses is still handshaked once.
 */
class CompositeBus(buses: List<SignalingBus>) : SignalingBus {
    private val active = buses.filter { it.available }
    override val label: String get() = active.joinToString(" + ") { it.label }.ifEmpty { "(none)" }
    override val available: Boolean get() = active.isNotEmpty()
    override fun start(onMessage: (dynamic) -> Unit) = active.forEach { it.start(onMessage) }
    override fun post(message: dynamic) = active.forEach { it.post(message) }
    override fun close() = active.forEach { it.close() }
}
